import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import {
  useAddContractByIDMutation,
  useGetAgentContractByIDQuery,
} from '@/apis/extended/agentApi';
import { useAddSellerContractByIDMutation } from '@/apis/extended/sellerAPI';

type SellerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  sellerId?: string;
};

export const SellerPropertyIdModal = ({
  isOpen,
  onClose,
  sellerId,
}: SellerModalProps) => {
  const theme = useTheme();
  const [inputId, setInputId] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [propertyId, setPropertyId] = useState<string | null>(null);

  const [addSellerContractByID] = useAddSellerContractByIDMutation();

  const verifyPropertyId = async () => {
    if (!inputId) {
      setError('Please enter a property ID.');
      return;
    }
    if (!sellerId) {
      setError('Missing seller ID.');
      return;
    }
    setIsLoading(true);
    try {
      await addSellerContractByID({ sellerId, contractId: inputId }).unwrap();
      setInputId('');
      onClose();
    } catch (error) {
      console.error('API Error:', error);
      setError(
        'Failed to add contract. Please check the contract ID and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogContent
        sx={{ py: '60px', px: '32px', width: '450px', maxWidth: '100%' }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Add New Contract
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.greyShades.grey1 }}
          >
            Enter the Contract ID to add it to your account.
          </Typography>
        </Box>
        <Box sx={{ mt: 5 }}>
          <TextField
            label="Contract ID"
            fullWidth
            size="small"
            value={inputId}
            onChange={(e) => {
              setInputId(e.target.value);
              setError('');
            }}
            helperText={error}
            error={!!error}
            disabled={isLoading}
            placeholder="Enter contract ID here..."
          />
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            onClick={verifyPropertyId}
            disabled={isLoading}
          >
            {isLoading ? 'Adding Contract...' : 'Add Contract'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
